import { Typography, Grid } from "@/components/app";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Logo from "@/components/icons/Logo";
import {
  Mail,
  Phone,
  MapPin,
  Instagram,
  Twitter,
  Youtube,
  Facebook,
} from "lucide-react";
import { ROUTES } from "@/lib/routes";

const footerLinks = {
  support: [
    { label: "Contact Us", href: ROUTES.CONTACT_US, isExternal: true },
    // { label: "FAQ", href: "#faq", isExternal: false },
  ],
  legal: [
    { label: "Privacy Policy", href: ROUTES.PRIVACY_POLICY, isExternal: true },
    {
      label: "Terms & Conditions",
      href: ROUTES.TERMS_CONDITIONS,
      isExternal: true,
    },
  ],
};

const socialLinks = [
  {
    icon: Instagram,
    href: "https://www.instagram.com/shree_jewellers_11?igsh=MTNrbnlreDU0MXlwZQ==",
    label: "Instagram",
  },
  // { icon: Twitter, href: "https://twitter.com", label: "Twitter" },
  // { icon: Youtube, href: "https://youtube.com", label: "YouTube" },
  {
    icon: Facebook,
    href: "https://www.facebook.com/share/1BTXrYFAR9/",
    label: "Facebook",
  },
];

export function Footer() {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
    isExternal: boolean,
  ) => {
    e.preventDefault();

    if (isExternal) {
      // Navigate to external page
      navigate(href);
    } else {
      // Handle hash navigation (scroll to section)
      if (location.pathname !== ROUTES.HOME) {
        navigate(ROUTES.HOME + href);
      } else {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
  };

  return (
    <footer className="bg-primary text-white">
      <div className="border-b border-background/10 py-6 lg:py-[30px]">
        <div className="wrapper">
          <div className="flex justify-between flex-wrap items-center gap-y-5">
            {/* Left Section - Logo and Description */}
            <div className="w-full max-w-[457px] md:w-2/5">
              <div className="space-y-6">
                <div className="w-[172px] lg:w-[230px]">
                  <Logo isCollapsed={false} />
                </div>
                <div>
                  <Typography
                    variant="overline"
                    className="text-white/50 max-w-sm font-normal"
                  >
                    India's most trusted jewellery shopping website.
                  </Typography>
                </div>

                {/* Contact Info */}
                <div className="space-y-2.5 md:space-y-4">
                  <a
                    href="mailto:info@smorajewellery.com"
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    <Mail className="h-5 w-5 text-white shrink-0" />
                    <Typography variant="overline" className="text-white">
                      info@smorajewellery.com
                    </Typography>
                  </a>
                  <a
                    href="tel:+917069257283"
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    <Phone className="h-5 w-5 text-white shrink-0" />
                    <Typography variant="overline" className="text-white">
                      +91 70692 57283
                    </Typography>
                  </a>
                  <a
                    href="https://maps.app.goo.gl/pRNds9x3oY2G9xKx6"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:opacity-80 transition-opacity"
                  >
                    <MapPin className="h-5 w-5 text-white shrink-0" />
                    <Typography variant="overline" className="text-white">
                      Mendarda, Gujarat, India - 362260
                    </Typography>
                  </a>
                </div>
              </div>
            </div>

            {/* Right Section - Links */}
            <div className="w-full md:max-w-[600px] md:w-3/5">
              <Grid container spacing={4}>
                {/* Support Links */}
                <Grid item xs={6}>
                  <Typography variant="h6" className="text-background mb-4">
                    Support
                  </Typography>
                  <div className="flex flex-col gap-3">
                    {footerLinks.support.map((link) => (
                      <a
                        key={link.label}
                        href={link.href}
                        onClick={(e) =>
                          handleNavClick(e, link.href, link.isExternal)
                        }
                        className="text-white/50 text-sm md:text-base hover:text-background transition-colors cursor-pointer"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                </Grid>

                {/* Legal Links */}
                <Grid item xs={6}>
                  <Typography variant="h6" className="text-background mb-4">
                    Legal
                  </Typography>
                  <div className="flex flex-col gap-3">
                    {footerLinks.legal.map((link) => (
                      <a
                        key={link.label}
                        href={link.href}
                        onClick={(e) =>
                          handleNavClick(e, link.href, link.isExternal)
                        }
                        className="text-white/50 text-sm md:text-base hover:text-background transition-colors cursor-pointer"
                      >
                        {link.label}
                      </a>
                    ))}
                  </div>
                </Grid>
              </Grid>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Copyright and Social */}
      <div className="wrapper">
        <div className="flex flex-wrap justify-between items-center gap-6 py-4 lg:py-[30px]">
          <Typography variant="body2" className="text-background/60">
            © {new Date().getFullYear()} Smora. All rights reserved.
          </Typography>

          {/* Social Links */}
          <div className="flex items-center gap-3">
            {socialLinks.map((social) => {
              const Icon = social.icon;
              return (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-[36px] h-[36px] rounded-[12px] bg-white/15 border border-white flex items-center justify-center hover:bg-background/10 transition-colors"
                  aria-label={social.label}
                >
                  <Icon className="h-5 w-5 text-white" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}
